import { type CreateMessagePayload, useCreateMessageMutation } from '@pba/react-chat-core';
import type { MessagePlaceholder } from 'models';
import { useCallback, useMemo } from 'react';
import { message } from 'ui';
import type { ChatInputAttachment } from '../chat-input';

const getMessagePayload = (attachments: ChatInputAttachment[] = []) =>
  attachments.flatMap((attachment) => {
    if (!attachment.fileId) {
      return [];
    }

    return [
      {
        meta: {
          alts: [],
          mimeType: attachment.contentType,
          name: attachment.filename,
        },
        ref: attachment.fileId,
        type: 'media' as const,
      },
    ];
  });

const isCreateMessagePayload = (item: MessagePlaceholder['payload'][number]): item is CreateMessagePayload =>
  item.type === 'media' && typeof item.ref === 'string' && typeof item.meta?.mimeType === 'string';

const getRetryMessagePayload = (payload: MessagePlaceholder['payload']): CreateMessagePayload[] =>
  payload.filter((item) => isCreateMessagePayload(item));

type SendChatMessageParameters = {
  attachments?: ChatInputAttachment[];
  chatroomId: string;
  messageText?: string;
  onError?: () => Promise<void> | void;
  onSuccess?: () => Promise<void> | void;
};

export const useSendChatMessage = () => {
  const { mutate: createMessage } = useCreateMessageMutation();

  const sendChatMessage = useCallback(
    ({ attachments, chatroomId, messageText, onError, onSuccess }: SendChatMessageParameters) => {
      createMessage(
        {
          chatroomId,
          data: {
            payload: getMessagePayload(attachments),
            text: messageText?.trim(),
          },
        },
        {
          async onError() {
            message.error("Something went wrong. Message couldn't be sent.");
            await onError?.();
          },
          async onSuccess() {
            await onSuccess?.();
          },
        },
      );
    },
    [createMessage],
  );

  const retryChatMessage = useCallback(
    (failedMessage: MessagePlaceholder) => {
      createMessage({
        chatroomId: failedMessage.chatroom,
        data: {
          _id: failedMessage.id,
          payload: getRetryMessagePayload(failedMessage.payload),
          text: failedMessage.text,
        },
      });
    },
    [createMessage],
  );

  return useMemo(() => ({ retryChatMessage, sendChatMessage }), [retryChatMessage, sendChatMessage]);
};
